import { startOfMonth, endOfMonth, format } from 'date-fns';

export const preparePendingExpenses = (recurringExpenses, currentDate) => {
  const startOfCurrentMonth = startOfMonth(currentDate);
  const endOfCurrentMonth = endOfMonth(currentDate);
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  return recurringExpenses
    .filter(exp => {
      if (exp.isPaused) return false;
      
      // Check if the expense has ended
      if (exp.endDate) {
        const endDate = new Date(exp.endDate);
        endDate.setHours(0, 0, 0, 0);
        if (endDate < today) return false;
      }
      
      // Only include if nextPaymentDate is in the future or today
      const nextPayment = new Date(exp.nextPaymentDate);
      nextPayment.setHours(0, 0, 0, 0);
      return nextPayment >= today;
    })
    .flatMap(exp => {
      const occurrences = [];
      let currentOccurrence = new Date(exp.nextPaymentDate);
      
      // For one-off payments, only include if they fall within the month
      if (exp.isOneOff) {
        if (currentOccurrence >= startOfCurrentMonth && 
            currentOccurrence <= endOfCurrentMonth && 
            currentOccurrence >= today) {
          occurrences.push({ ...exp, nextPaymentDate: currentOccurrence.toISOString() });
        }
        return occurrences;
      }

      // Get end date if it exists
      const endDate = exp.endDate ? new Date(exp.endDate) : null;
      endDate?.setHours(23, 59, 59, 999); // Set to end of day

      // For recurring payments, calculate all occurrences within the month
      while (currentOccurrence <= endOfCurrentMonth) {
        // Stop if we've passed the end date
        if (endDate && currentOccurrence > endDate) break;

        if (currentOccurrence >= startOfCurrentMonth && currentOccurrence >= today) {
          occurrences.push({ 
            ...exp, 
            nextPaymentDate: currentOccurrence.toISOString() 
          });
        }

        // Calculate next occurrence based on period
        const normalizedUnit = exp.period.unit.toLowerCase().replace(/s$/, '');
        const nextOccurrence = new Date(currentOccurrence);
        
        switch (normalizedUnit) {
          case 'day':
            nextOccurrence.setDate(nextOccurrence.getDate() + parseInt(exp.period.number));
            break;
          case 'week':
            nextOccurrence.setDate(nextOccurrence.getDate() + (parseInt(exp.period.number) * 7));
            break;
          case 'month':
            nextOccurrence.setMonth(nextOccurrence.getMonth() + parseInt(exp.period.number));
            break;
          case 'year':
            nextOccurrence.setFullYear(nextOccurrence.getFullYear() + parseInt(exp.period.number));
            break;
        }
        currentOccurrence = nextOccurrence;
      }

      return occurrences;
    })
    .map(exp => {
      const nextPayment = new Date(exp.nextPaymentDate);
      nextPayment.setHours(0, 0, 0, 0);

      const daysUntil = Math.ceil((nextPayment - today) / (1000 * 60 * 60 * 24));

      const frequencyText = exp.isOneOff 
        ? 'One-off payment'
        : `Every ${exp.period.number} ${exp.period.unit}${exp.period.number > 1 ? 's' : ''}`;

      return {
        ...exp,
        daysUntil,
        status: daysUntil <= 0 ? 'Due' : 'Upcoming',
        frequency: frequencyText,
        nextPaymentFormatted: format(nextPayment, 'dd MMM yyyy')
      };
    })
    .sort((a, b) => a.daysUntil - b.daysUntil);
};
